import React, { useEffect, useMemo, useRef } from 'react';
import { path as ramdaPath, pick } from 'ramda';

import { Redirect } from 'js/components/common/Redirect';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { JumioPopup } from 'js/components/system/Page/buzz/components/JumioPopup';
import { OccupationPopup } from '@interlayer/js/app/components/popups/OccupationPopup/OccupationPopup';
import { OldBrowserMessagePopup } from 'js/components/system/Page/buzz/components/OldBrowserMessagePopup';

import { useNavigate } from 'js/hooks/navigate';
import { useAppSelector, useAuthSelector, useUserSelector } from 'js/store/hooks';
import { useLoginPopup } from 'js/components/popups/LoginPopup/hooks';
import { getCleanUrl } from 'js/utils/url.util';
import * as hooks from 'js/components/system/Page/hocs/withCommonPage/hooks';
import { useCashierMessages } from 'js/app/hooks/user/hooks/useCashierMessages';
import { useVerificationCardPin } from 'js/app/hooks/user/hooks/useVerificationCardPin';
import { PageContext } from 'js/components/system/Page/hocs/withCommonPage/context.buzz.ts';
import { useRegistrationDestroyed } from 'js/app/components/main/Registration/hooks/useRegistrationDestroyed';
import { useHideJumioPopup } from 'js/components/system/Page/buzz/components/JumioPopup/hooks/useHideJumioPopup';
import { useCheckOccupation, useTriggerOccupationPopup } from '@interlayer/js/app/hooks/user/hooks/useCheckOccupation';

import style from 'js/components/system/Page/hocs/withCommonPage/withCommonPage.scss';

export const withCommonPage = (WrappedComponent, isPageInPopup) => (props) => {
    const { path, isPopupBackgroundPage, isLoginPage } = props;
    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const { http404Configuration, rootPage, loginPage, redirects } = useAppSelector(pick([
        'http404Configuration', 'rootPage', 'loginPage', 'redirects'
    ]));
    const ageVerificationStatus = useUserSelector(ramdaPath(['userDetails', 'ageVerificationStatus']));
    const prevPage = useRef(null);
    const isShowedOnCurrentPage = useRef(false);
    const {
        isLoggedIn, role, isShowOccupationPopup, isOccupationSet
    } = useUserSelector(pick(['isLoggedIn', 'role', 'isShowOccupationPopup', 'isOccupationSet']));
    const [{ isLoading, pageError, page }, fetchPageInfo] = hooks.usePageInfoFetch({ path, isPageInPopup });
    const { isLogoutInProcess, isLoginInProcess } = useAuthSelector(pick(['isLogoutInProcess', 'isLoginInProcess']));
    const { error, is404Error, is403Error } = pageError || {};
    const checkOccupation = useCheckOccupation({ shouldCheckRequested: false });
    hooks.useForwardRootPage({ page });
    hooks.usePageAsPopupBackground({ isPageInPopup, path, page, pageError });

    const isHideJumioPopup = useHideJumioPopup();
    useCashierMessages();
    useVerificationCardPin();
    useRegistrationDestroyed();
    useTriggerOccupationPopup({ shouldCheckRequested: true, shouldCheckForce: true });

    useEffect(() => {
        if (prevPage.current !== path) {
            prevPage.current = path;
            isShowedOnCurrentPage.current = false;
        }
    }, [path]);

    useEffect(() => {
        if (page?.rootClassNames.includes('show-occupation-popup') && !isShowedOnCurrentPage.current) {
            checkOccupation();
            isShowedOnCurrentPage.current = true;
        }
    }, [page?.rootClassNames, checkOccupation]);

    useEffect(() => {
        if (getCleanUrl() === loginPage || isLoginPage) {
            if (isLoggedIn) {
                navigate(rootPage);
            } else {
                showLoginPopup({
                    onClose: () => navigate(rootPage),
                    onLogin: () => navigate(path || rootPage)
                });
            }
        }

        if (!is403Error || role === 'admin' || isPopupBackgroundPage || pageError.path !== path) {
            return;
        }

        const { permissions } = error;
        const { user, guest } = permissions;

        if (user && !guest && !isLoggedIn && !isLogoutInProcess) {
            showLoginPopup({
                onClose: (url) => {
                    navigate(url || rootPage);
                },
                onLogin: () => navigate(path)
            });
        }
    }, [
        isLoggedIn, role, is403Error, error, loginPage, path, navigate, showLoginPopup,
        isLogoutInProcess, rootPage, isPopupBackgroundPage, pageError?.path, isLoginPage
    ]);

    const pageContextValue = useMemo(() => page || props, [page, props]);

    if (is404Error) {
        const { http404RedirectionType, url, page: page404 } = http404Configuration;

        switch (http404RedirectionType) {
            case 'URL':
                return <Redirect to={url} />;

            case 'PAGE':
                return <Redirect to={page404} />;

            default:
                if (path === rootPage) {
                    const errorMessage = typeof error === 'string' || error instanceof window.Error ? error.toString() : '';

                    return !isPageInPopup ? <ErrorMessage className={style.error} message={errorMessage}/> : null;
                }

                return <Redirect to={rootPage} />;
        }
    }

    if (is403Error) {
        if (isPopupBackgroundPage || pageError.path !== path) {
            return null;
        }

        const isRootPageRedirect = Object.entries(redirects)
            .find(([fromPage, toPage]) => toPage === path && fromPage === rootPage);

        if (isRootPageRedirect) {
            return null;
        }

        const { permissions } = error;
        const { user, guest } = permissions;

        if (!user && !guest) {
            return <Redirect to={rootPage} />;
        }

        if (!user && guest && isLoggedIn) {
            return <Redirect to={rootPage} />;
        }

        // cannot redirect here, because user state changes async
        return null;
    }

    if (error && !isPageInPopup) {
        const errorMessage = typeof error === 'string' || error instanceof window.Error
            ? error.toString()
            : 'playtech.mobile.system.error.no.response';

        return <ErrorMessage className={style.error} message={errorMessage} />;
    }

    return (
        <PageContext.Provider value={pageContextValue}>
            <WrappedComponent
                {...props}
                page={page}
                error={error}
                isLoading={isLoading}
                fetchPageInfo={fetchPageInfo}
            />
            {!isHideJumioPopup && <JumioPopup />}
            {
                isShowOccupationPopup
                && !isOccupationSet
                && !isLoginInProcess
                && ageVerificationStatus === 'passed'
                && <OccupationPopup />
            }
            <OldBrowserMessagePopup />
        </PageContext.Provider>
    );
};
